'use client';

import { getResourceData } from '@/api/public';
import logger from '@/utils/client-logger';
import type {
  PDCardCalibrationFaceGateLimits,
  PDCardCalibrationGateLimits,
} from '@zeelool/pd-card-calibration';
import type { FaceMeasurementMode } from './types';

const MEASUREMENT_SDK_CONFIG_TIMEOUT_MS = 2_000;
const MEASUREMENT_SDK_CONFIG_KEY = 'pd-card-calibration';

const FACE_GATE_LIMIT_KEYS = [
  'faceRollMaxDeg',
  'faceYawMaxDeg',
  'facePitchMaxDeg',
  'faceDistanceMinAreaRatio',
  'faceDistanceMaxAreaRatio',
] as const satisfies readonly (keyof PDCardCalibrationFaceGateLimits)[];

const CARD_GATE_LIMIT_KEYS = [
  'cardDistanceCloseMaxRatio',
  'cardDistanceFarMaxRatio',
  'cardRatioMaxError',
] as const satisfies readonly Exclude<
  keyof PDCardCalibrationGateLimits,
  keyof PDCardCalibrationFaceGateLimits
>[];

const ANGLE_GATE_LIMIT_KEYS = new Set<MeasurementSdkFaceGateLimitKey>([
  'faceRollMaxDeg',
  'faceYawMaxDeg',
  'facePitchMaxDeg',
]);

type MeasurementSdkFaceGateLimitKey = (typeof FACE_GATE_LIMIT_KEYS)[number];
type MeasurementSdkCardGateLimitKey = (typeof CARD_GATE_LIMIT_KEYS)[number];
type MeasurementSdkGateLimitKey = MeasurementSdkFaceGateLimitKey | MeasurementSdkCardGateLimitKey;

export type MeasurementSdkGateLimits = PDCardCalibrationGateLimits;

let cachedGateLimits: MeasurementSdkGateLimits | undefined;
let inFlightGateLimitsPromise: Promise<MeasurementSdkGateLimits | undefined> | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseGateLimits(value: unknown): MeasurementSdkGateLimits | undefined {
  if (!isRecord(value)) return undefined;

  const gateLimits: Partial<Record<MeasurementSdkGateLimitKey, number>> = {};
  const gateLimitKeys: readonly MeasurementSdkGateLimitKey[] = [
    ...FACE_GATE_LIMIT_KEYS,
    ...CARD_GATE_LIMIT_KEYS,
  ];

  for (const key of gateLimitKeys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return undefined;
    const limit = value[key];
    if (
      typeof limit !== 'number' ||
      !Number.isFinite(limit) ||
      limit <= 0 ||
      (ANGLE_GATE_LIMIT_KEYS.has(key as MeasurementSdkFaceGateLimitKey) && limit > 180)
    ) {
      return undefined;
    }
    gateLimits[key] = limit;
  }

  if (
    gateLimits.faceDistanceMinAreaRatio === undefined ||
    gateLimits.faceDistanceMaxAreaRatio === undefined ||
    gateLimits.faceDistanceMinAreaRatio >= gateLimits.faceDistanceMaxAreaRatio
  ) {
    return undefined;
  }

  return gateLimits as MeasurementSdkGateLimits;
}

function extractGateLimits(response: unknown): MeasurementSdkGateLimits | undefined {
  if (!isRecord(response) || response.code !== 200) {
    logger.warn('[FaceMeasurement] Failed to load SDK gate limits', { response });
    return undefined;
  }

  const data = response.data;
  const jsonvalues = isRecord(data) ? data.jsonvalues : undefined;
  const config = isRecord(jsonvalues) ? jsonvalues[MEASUREMENT_SDK_CONFIG_KEY] : undefined;
  const gateLimits = parseGateLimits(isRecord(config) ? config.gateLimits : undefined);

  if (!gateLimits) {
    logger.warn('[FaceMeasurement] Invalid SDK gate limits config', { config });
  }

  return gateLimits;
}

function requestMeasurementSdkGateLimits(): Promise<MeasurementSdkGateLimits | undefined> {
  if (cachedGateLimits) return Promise.resolve(cachedGateLimits);
  if (inFlightGateLimitsPromise) return inFlightGateLimitsPromise;

  const request = Promise.resolve()
    .then(() =>
      getResourceData({
        jsonvalues: [MEASUREMENT_SDK_CONFIG_KEY],
      }),
    )
    .then((response: unknown) => {
      const gateLimits = extractGateLimits(response);
      if (gateLimits && !cachedGateLimits) cachedGateLimits = gateLimits;
      return gateLimits;
    })
    .catch((error: unknown) => {
      logger.warn('[FaceMeasurement] Failed to load SDK gate limits', { error });
      return undefined;
    })
    .finally(() => {
      if (inFlightGateLimitsPromise === request) inFlightGateLimitsPromise = null;
    });

  inFlightGateLimitsPromise = request;
  return request;
}

async function requestGateLimitsWithinTimeout(): Promise<MeasurementSdkGateLimits | undefined> {
  const request = requestMeasurementSdkGateLimits();
  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => {
      timedOut = true;
      if (inFlightGateLimitsPromise === request) inFlightGateLimitsPromise = null;
      logger.warn('[FaceMeasurement] SDK gate limits request timed out', {
        timeoutMs: MEASUREMENT_SDK_CONFIG_TIMEOUT_MS,
      });
      resolve(undefined);
    }, MEASUREMENT_SDK_CONFIG_TIMEOUT_MS);
  });

  const gateLimits = await Promise.race([request, timeout]);
  if (!timedOut && timeoutId !== undefined) clearTimeout(timeoutId);
  return gateLimits;
}

export async function loadMeasurementSdkGateLimits(
  mode: FaceMeasurementMode,
): Promise<MeasurementSdkGateLimits | undefined> {
  const gateLimits = await requestGateLimitsWithinTimeout();
  if (!gateLimits) return undefined;

  if (mode === 'card') return gateLimits;

  return Object.fromEntries(FACE_GATE_LIMIT_KEYS.map((key) => [key, gateLimits[key]])) as Pick<
    MeasurementSdkGateLimits,
    MeasurementSdkFaceGateLimitKey
  >;
}
